import React, { useEffect, useState } from 'react';
import { Box, Typography, LinearProgress, Fade, keyframes } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  getQueryLoadingStepDelayMs,
  QUERY_LOADING_PROGRESS_TICK_MS,
  QUERY_LOADING_STEPS,
  QUERY_LOADING_TOTAL_DURATION_MS,
} from './queryLoadingSteps';

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

interface QueryLoadingOverlayProps {
  query: string;
  'data-testid'?: string;
}

const QueryLoadingOverlay: React.FC<QueryLoadingOverlayProps> = ({
  query,
  'data-testid': testId = 'docs-query-loading-overlay',
}) => {
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    setActiveStepIndex(0);
    setProgressPercent(0);

    const stepTimeoutIds = QUERY_LOADING_STEPS.map((_, stepIndex) => {
      if (stepIndex === 0) {
        return undefined;
      }
      return window.setTimeout(() => {
        setActiveStepIndex(stepIndex);
      }, getQueryLoadingStepDelayMs(stepIndex));
    }).filter((timeoutId): timeoutId is number => timeoutId !== undefined);

    const progressIntervalId = window.setInterval(() => {
      const elapsedMs = Date.now() - startedAt;
      const cappedElapsedMs = Math.min(elapsedMs, QUERY_LOADING_TOTAL_DURATION_MS);
      setProgressPercent((cappedElapsedMs / QUERY_LOADING_TOTAL_DURATION_MS) * 100);
    }, QUERY_LOADING_PROGRESS_TICK_MS);

    return () => {
      stepTimeoutIds.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      window.clearInterval(progressIntervalId);
    };
  }, [query]);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Searching documents"
      data-testid={testId}
      sx={{
        p: 4,
        mb: 3,
        borderRadius: '0.5rem',
        border: '1px solid #D9D9D9',
        bgcolor: '#FFFFFF',
        boxShadow: '-1px 2px 12px rgba(11, 147, 223, 0.12)',
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: '#FFFFFF',
            animation: `${pulseAnimation} 1.6s ease-in-out infinite`,
          }}
          aria-hidden="true"
        >
          <AutoAwesomeIcon />
        </Box>
        <Box>
          <Typography variant="h6" component="p" sx={{ fontWeight: 600, mb: 0.5 }}>
            Searching your knowledge base
          </Typography>
          <Typography variant="body2" color="text.secondary" data-testid={`${testId}-query`}>
            &ldquo;{query}&rdquo;
          </Typography>
        </Box>
      </Box>

      <LinearProgress
        variant="determinate"
        value={progressPercent}
        aria-label="Search progress"
        data-testid={`${testId}-progress`}
        sx={{
          height: 6,
          borderRadius: 3,
          mb: 3,
          bgcolor: '#E8F4FC',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            background: 'linear-gradient(90deg, #0B93DF 0%, #4DB8F0 50%, #0B93DF 100%)',
            backgroundSize: '200% 100%',
            animation: `${shimmerAnimation} 1.8s linear infinite`,
          },
        }}
      />

      <Box
        component="ul"
        sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        {QUERY_LOADING_STEPS.map((step, stepIndex) => {
          const isCompleted = stepIndex < activeStepIndex;
          const isActive = stepIndex === activeStepIndex;

          return (
            <Fade in key={step.label} timeout={400}>
              <Box
                component="li"
                display="flex"
                alignItems="center"
                gap={1.5}
                data-testid={`${testId}-step`}
                sx={{
                  opacity: stepIndex > activeStepIndex ? 0.45 : 1,
                  transition: 'opacity 0.3s ease',
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                  ) : (
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: isActive ? 'primary.main' : '#BDBDBD',
                        animation: isActive ? `${pulseAnimation} 1.2s ease-in-out infinite` : 'none',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
            </Fade>
          );
        })}
      </Box>
    </Box>
  );
};

export default QueryLoadingOverlay;
